namespace $ {

	export type $shm_hitalama_map_journal_snapshots_geometry_event = {
		type: 'geometry'
		widget: string
		local_time: number
		time: number
		recalculation: 'read' | 'write'
		geometry: {
			body_x: number
			body_y: number
			right_edge_x: number
			bottom_edge_y: number
		}
	}

	export type $shm_hitalama_map_journal_snapshots_snapshot = {
		run: string
		client: string
		client_info: $shm_hitalama_map_journal_snapshots_client_info
		time_offset: number
		written_at: number
		events: readonly $shm_hitalama_map_journal_snapshots_geometry_event[]
	}

	export type $shm_hitalama_map_journal_snapshots_client_info = {
		client_id: string
		name?: string
		device?: string
		browser?: string
		platform?: string
		user_agent?: string
	}

	export class $shm_hitalama_map_journal_snapshots extends $giper_baza_entity.with({

		Write_started: $giper_baza_atom_bool,
		Write_stopped: $giper_baza_atom_bool,
		Started_at: $giper_baza_atom_real,
		Stopped_at: $giper_baza_atom_real,
		Run_id: $giper_baza_atom_text,
		Clients: $giper_baza_list_str,
		Client_infos: $giper_baza_dict_to( $giper_baza_atom_dict ),
		Snapshots: $giper_baza_dict_to( $giper_baza_atom_dict ),

	}) {

		private static write_done = new WeakMap< $shm_hitalama_map_journal_snapshots, Set< string > >

		write_started( next?: boolean ) {
			return this.Write_started( next )?.val( next ) ?? false
		}

		write_stopped( next?: boolean ) {
			return this.Write_stopped( next )?.val( next ) ?? false
		}

		started_at( next?: number ) {
			return this.Started_at( next )?.val( next ) ?? 0
		}

		stopped_at( next?: number ) {
			return this.Stopped_at( next )?.val( next ) ?? 0
		}

		run_id( next?: string ) {
			return this.Run_id( next )?.val( next ) ?? ''
		}

		clients( next?: readonly string[] ) {
			return ( this.Clients( next )?.items( next ) ?? [] ) as string[]
		}

		client_id() {
			return $giper_baza_auth.current().pass().peer().str
		}

		client_name() {
			return this.$.$shm_hitalama_profile.current()?.Login()?.val() || ''
		}

		client_device() {
			const navigator = this.$.$mol_dom_context.navigator
			const user_agent = navigator?.userAgent ?? ''
			const platform = navigator?.platform ?? ''
			const source = `${ user_agent } ${ platform }`.toLowerCase()
			if( /android/.test( source ) ) return 'Android'
			if( /iphone|ipad|ipod/.test( source ) ) return 'iOS'
			if( /windows/.test( source ) ) return 'Windows'
			if( /macintosh|mac os/.test( source ) ) return 'macOS'
			if( /linux/.test( source ) ) return 'Linux'
			return platform || 'unknown'
		}

		client_browser() {
			const user_agent = this.$.$mol_dom_context.navigator?.userAgent ?? ''
			if( /Edg\//.test( user_agent ) ) return 'Edge'
			if( /OPR\//.test( user_agent ) ) return 'Opera'
			if( /Chrome\//.test( user_agent ) ) return 'Chrome'
			if( /Firefox\//.test( user_agent ) ) return 'Firefox'
			if( /Safari\//.test( user_agent ) ) return 'Safari'
			return 'unknown'
		}

		client_info_make( client = this.client_id() ): $shm_hitalama_map_journal_snapshots_client_info {
			const navigator = this.$.$mol_dom_context.navigator
			const name = this.client_name()
			return {
				client_id: client,
				... name ? { name } : {},
				device: this.client_device(),
				browser: this.client_browser(),
				platform: navigator?.platform ?? '',
				user_agent: navigator?.userAgent ?? '',
			}
		}

		client_info(
			client = this.client_id(),
			next?: $shm_hitalama_map_journal_snapshots_client_info,
		): $shm_hitalama_map_journal_snapshots_client_info | null {
			return this.Client_infos( next )?.key( client, next === undefined ? undefined : 'auto' )?.val( next ) as any ?? null
		}

		@ $mol_action
		client_add( client = this.client_id() ) {
			const clients = this.clients()
			if( !clients.includes( client ) ) this.clients([ ... clients, client ])
			this.client_info( client, this.client_info_make( client ) )
			return client
		}

		@ $mol_action
		write_start() {
			this.run_id( `${ Date.now() }_${ Math.random().toString( 36 ).slice( 2 ) }` )
			this.clients( [] )
			this.write_done_reset()
			this.write_stopped( false )
			this.stopped_at( 0 )
			this.started_at( Date.now() )
			this.write_started( true )
		}

		@ $mol_action
		write_stop() {
			this.stopped_at( Date.now() )
			this.write_stopped( true )
		}

		snapshot( client = this.client_id(), next?: $shm_hitalama_map_journal_snapshots_snapshot ) {
			return this.Snapshots( next )?.key( client, next === undefined ? undefined : 'auto' )?.val( next )
		}

		@ $mol_mem
		time_offset() {
			const local_started = Date.now()
			const data = this.$.$mol_fetch.json( 'https://timeapi.io/api/Time/current/zone?timeZone=UTC', { cache: 'no-store' } )
			const remote = this.time_remote_from( data )
			const local_finished = Date.now()
			const local_middle = local_started + ( local_finished - local_started ) / 2
			return remote - local_middle
		}

		time_remote_from( data: any ) {
			const unix_ms = Number( data?.unixTime ?? data?.unixtime )
			if( Number.isFinite( unix_ms ) && unix_ms > 0 ) {
				return unix_ms < 1_000_000_000_000 ? unix_ms * 1000 : unix_ms
			}

			const iso = data?.dateTime ?? data?.currentDateTime ?? data?.datetime
			const iso_utc = typeof iso === 'string' && !/[zZ]|[+-]\d\d:?\d\d$/.test( iso )
				? `${ iso }Z`
				: iso
			const remote = Date.parse( iso_utc )
			if( Number.isFinite( remote ) ) return remote

			return Date.now()
		}

		write_done( client = this.client_id() ) {
			let done = $shm_hitalama_map_journal_snapshots.write_done.get( this )
			if( !done ) {
				done = new Set
				$shm_hitalama_map_journal_snapshots.write_done.set( this, done )
			}
			return done.has( client )
		}

		write_done_mark( client = this.client_id() ) {
			let done = $shm_hitalama_map_journal_snapshots.write_done.get( this )
			if( !done ) {
				done = new Set
				$shm_hitalama_map_journal_snapshots.write_done.set( this, done )
			}
			done.add( client )
		}

		write_done_reset() {
			$shm_hitalama_map_journal_snapshots.write_done.delete( this )
		}

		write_if_requested(
			snapshot_make: ( offset: number, client: string, run: string )=> $shm_hitalama_map_journal_snapshots_snapshot,
		) {
			if( !this.write_stopped() ) {
				this.write_done_reset()
				return
			}

			const client = this.client_id()
			if( this.write_done( client ) ) return

			const offset = this.time_offset()

			this.snapshot( client, snapshot_make( offset, client, this.run_id() ) )
			this.write_done_mark( client )
		}

		snapshots_written_count() {
			const run = this.run_id()
			return this.clients().filter( client => this.snapshot( client )?.run === run ).length
		}

	}

}
